//
// usePauseWsDeliveryWhileUnfocused
//
// notif-anon sends no push to an app it reaches over its WebSocket, so a page
// nobody is looking at would swallow the notification. While unfocused, the
// page declares its socket paused (wsPause / wsResume, "WebSocket Pause /
// Resume Protocol" in global/docs/Paxity notifications.md): the push goes out
// and public/service-worker.js, which displays when no window is focused,
// shows it. Returns whether delivery on the current socket is paused.
//
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { browserHoldsPushSubscription } from '../webApp/utils';

const usePauseWsDeliveryWhileUnfocused = (wsRef, wsSocketOpen) => {
  const [wsPaused, setWsPaused] = useState(false);
  const pausedWsRef = useRef(null);
  const holdsPushSubscriptionRef = useRef(false);

  const applyFocusToDelivery = useCallback(
    () => {
      const ws = wsRef.current;
      if (ws?.readyState !== WebSocket.OPEN) return;

      const unfocused = (document.visibilityState !== 'visible') || (!document.hasFocus());
      // Without a push subscription nothing would take over from the socket
      const pause = unfocused && holdsPushSubscriptionRef.current;
      if (pause !== (pausedWsRef.current === ws)) {
        ws.send(JSON.stringify({ type: (pause) ? 'wsPause' : 'wsResume' }));
      }
      pausedWsRef.current = (pause) ? ws : null;
      setWsPaused(pause);
    },
    [wsRef],
  );

  // A page about to be frozen must send its pause at once, so the last known
  // subscription state decides first and the lookup corrects it afterwards
  const followFocus = useCallback(
    async () => {
      applyFocusToDelivery();
      holdsPushSubscriptionRef.current = await browserHoldsPushSubscription();
      applyFocusToDelivery();
    },
    [applyFocusToDelivery],
  );

  useEffect(
    () => {
      window.addEventListener('focus', followFocus);
      window.addEventListener('blur', followFocus);
      document.addEventListener('visibilitychange', followFocus);

      return () => {
        window.removeEventListener('focus', followFocus);
        window.removeEventListener('blur', followFocus);
        document.removeEventListener('visibilitychange', followFocus);
      };
    },
    [followFocus],
  );

  // The pause lives on the connection: notif-anon starts every new socket unpaused
  useEffect(
    () => {
      // Use void to make it clear we do not want to wait this async function:
      if (wsSocketOpen) void followFocus();
    },
    [wsSocketOpen, followFocus],
  );

  return wsPaused;
};

export default usePauseWsDeliveryWhileUnfocused;
